// client/src/components/templates/mensagens/TemplateMensagemPreview.js - VERSÃO COMPLETA CORRIGIDA
import React, { useState, useRef } from 'react';
import { FileText, Download, Printer, Eye, EyeOff, AlertCircle, Loader2, ArrowLeft, XCircle, Copy, MessageSquare } from 'lucide-react';
import useAlert from '../../../hooks/useAlert';

const TemplateMensagemPreview = ({ template, onClose }) => {
    const { setAlert } = useAlert();
    const [showPreview, setShowPreview] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const iframeRef = useRef(null);

    if (!template) {
        return (
            <div className="content-panel">
                <div className="panel-state-wrapper">
                    <FileText size={48} className="text-gray-400" />
                    <p>Nenhuma mensagem selecionada para visualização</p>
                </div>
            </div>
        );
    }

    const handlePrint = () => {
        setIsLoading(true);
        const printContent = createPrintContent();
        const printWindow = window.open('', '_blank');
        
        if (!printWindow) {
            setAlert('Não foi possível abrir a janela de impressão. Verifique se o pop-up está bloqueado.', 'warning');
            setIsLoading(false);
            return;
        }
        
        printWindow.document.write(printContent);
        printWindow.document.close();
        
        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            setIsLoading(false);
        }, 500);
    };

    const handleDownload = () => {
        setIsLoading(true);
        const content = createPrintContent();
        const blob = new Blob([content], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mensagem-${template.nome}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setIsLoading(false);
    };

    const handleCopyContent = async () => {
        try {
            if (template.conteudo) {
                await navigator.clipboard.writeText(template.conteudo);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }
        } catch (error) {
            console.error('Erro ao copiar conteúdo:', error);
            setAlert('Erro ao copiar conteúdo para a área de transferência', 'danger');
        }
    };

    const togglePreview = () => {
        setShowPreview(!showPreview);
    };

    const createPrintContent = () => {
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Mensagem: ${template.nome}</title>
                <style>
                    @page {
                        size: A4 portrait;
                        margin: 20mm;
                    }
                    
                    body {
                        font-family: Arial, Helvetica, sans-serif;
                        color: #222;
                        line-height: 1.4;
                        margin: 0;
                        padding: 0;
                        background: white;
                        width: 210mm;
                        min-height: 297mm;
                        box-sizing: border-box;
                    }
                    
                    .mensagem-container {
                        padding: 15mm;
                        min-height: 257mm;
                        background: white;
                    }
                    
                    .mensagem-header {
                        border-bottom: 2px solid #0b63d6;
                        padding-bottom: 10px;
                        margin-bottom: 20px;
                    }
                    
                    .mensagem-title {
                        font-size: 24px;
                        color: #0b63d6;
                        margin: 0 0 10px 0;
                    }
                    
                    .mensagem-meta {
                        display: flex;
                        gap: 20px;
                        margin-bottom: 10px;
                    }
                    
                    .mensagem-meta-item {
                        font-size: 14px;
                        color: #666;
                    }
                    
                    .mensagem-content {
                        margin-top: 20px;
                        padding: 20px;
                        background: #f8fafc;
                        border-radius: 8px;
                        border: 1px solid #e2e8f0;
                    }
                    
                    .mensagem-html-content {
                        font-family: Arial, Helvetica, sans-serif;
                        line-height: 1.6;
                    }
                    
                    .badge {
                        display: inline-block;
                        padding: 4px 8px;
                        background: #3b82f6;
                        color: white;
                        border-radius: 4px;
                        font-size: 12px;
                        font-weight: 600;
                        margin-left: 8px;
                    }
                    
                    .page-number {
                        position: absolute;
                        bottom: 10mm;
                        right: 15mm;
                        font-size: 12px;
                        color: #666;
                    }
                </style>
            </head>
            <body>
                <div class="mensagem-container">
                    <div class="mensagem-header">
                        <h1 class="mensagem-title">${template.nome}</h1>
                        <div class="mensagem-meta">
                            <span class="mensagem-meta-item">
                                <strong>Canal:</strong> 
                                <span class="badge">${template.canal}</span>
                            </span>
                            <span class="mensagem-meta-item">
                                <strong>Categoria:</strong> ${template.categoria || 'Não definida'}
                            </span>
                            <span class="mensagem-meta-item">
                                <strong>Status:</strong> ${template.ativo ? 'Ativo' : 'Inativo'}
                            </span>
                        </div>
                    </div>
                    
                    <div class="mensagem-content">
                        <h3>Conteúdo da Mensagem</h3>
                        <div class="mensagem-html-content">
                            ${template.conteudo || '<p>Nenhum conteúdo definido.</p>'}
                        </div>
                    </div>
                    
                    <div class="page-number">Página 1 de 1</div>
                </div>
            </body>
            </html>
        `;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('pt-BR');
    };

    const getCanalBadgeColor = (canal) => {
        const colors = {
            'email': 'badge-blue',
            'sms': 'badge-green',
            'whatsapp': 'badge-green',
            'push': 'badge-orange',
            'inapp': 'badge-purple'
        };
        return colors[canal] || 'badge-gray';
    };

    return (
        <div className="content-panel">
            {/* CABEÇALHO NO ESTILO DO FORMULÁRIO */}
            <div className="form-header">
                <h3 className="form-title">Visualizando: {template.nome}</h3>
                <button 
                    type="button" 
                    onClick={onClose} 
                    title="Fechar" 
                    className="form-close-button"
                >
                    <XCircle size={24} />
                </button>
            </div>

            {/* RESUMO DA MENSAGEM */}
            <fieldset className="form-section">
                <legend className="form-section-title">Resumo da Mensagem</legend>
                <div className="form-grid-container-auto-fit">
                    <div className="form-group">
                        <label className="form-label">Nome:</label>
                        <div className="form-input form-input-disabled">{template.nome}</div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Canal:</label>
                        <div className="form-input form-input-disabled">
                            <span className={`badge ${getCanalBadgeColor(template.canal)}`}>
                                {template.canal}
                            </span>
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Categoria:</label>
                        <div className="form-input form-input-disabled">
                            {template.categoria || 'Não definida'}
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Status:</label>
                        <div className="form-input form-input-disabled">
                            <span className={`badge ${template.ativo ? 'badge-green' : 'badge-gray'}`}>
                                {template.ativo ? 'Ativo' : 'Inativo'}
                            </span>
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Caracteres:</label>
                        <div className="form-input form-input-disabled highlight">
                            {template.conteudo?.length || 0}
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Modificado em:</label>
                        <div className="form-input form-input-disabled">
                            {formatDate(template.updatedAt)}
                        </div>
                    </div>
                </div>
            </fieldset>

            {/* CONTROLES DE VISUALIZAÇÃO */}
            <fieldset className="form-section">
                <legend className="form-section-title">Controles de Visualização</legend>
                <div className="editor-toolbar">
                    <button 
                        onClick={togglePreview}
                        className="action-button action-button-secondary"
                        disabled={isLoading}
                    >
                        {showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
                        {showPreview ? 'Ocultar Preview' : 'Mostrar Preview'}
                    </button>

                    <button 
                        onClick={handleCopyContent}
                        className="action-button action-button-secondary"
                        disabled={!template.conteudo}
                    >
                        <Copy size={16} />
                        {copied ? 'Copiado!' : 'Copiar Conteúdo'}
                    </button>
                    
                    <button 
                        onClick={handleDownload}
                        className="action-button action-button-secondary"
                        disabled={isLoading || !template.conteudo}
                    >
                        {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                        Download
                    </button>
                    
                    <button 
                        onClick={handlePrint}
                        className="action-button action-button-primary"
                        disabled={isLoading || !template.conteudo}
                    >
                        {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
                        Imprimir
                    </button>
                </div>
            </fieldset>

            {/* PREVIEW DO CONTEÚDO */}
            {showPreview && (
                <fieldset className="form-section">
                    <legend className="form-section-title">
                        Preview da Mensagem
                    </legend>
                    
                    {isLoading ? (
                        <div className="panel-state-wrapper">
                            <Loader2 size={48} className="animate-spin text-blue-500" />
                            <p>Processando mensagem...</p>
                        </div>
                    ) : !template.conteudo ? (
                        <div className="panel-state-wrapper error">
                            <AlertCircle size={48} className="text-yellow-500" />
                            <h4>Mensagem Sem Conteúdo</h4>
                            <p>Esta mensagem não possui conteúdo para visualização.</p>
                        </div>
                    ) : (
                        <div className="preview-mensagem-container">
                            {/* HEADER DA MENSAGEM */}
                            <div className="mensagem-preview-header">
                                <div className="mensagem-icon">
                                    <MessageSquare size={24} className="text-blue-500" />
                                </div>
                                <div className="mensagem-info">
                                    <h4 className="mensagem-preview-title">{template.nome}</h4>
                                    <div className="mensagem-preview-meta">
                                        <span className="meta-item">
                                            <strong>Canal:</strong> 
                                            <span className={`badge ${getCanalBadgeColor(template.canal)}`}>
                                                {template.canal}
                                            </span>
                                        </span>
                                        {template.categoria && (
                                            <span className="meta-item">
                                                <strong>Categoria:</strong> {template.categoria}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* CONTEÚDO DA MENSAGEM */}
                            <div className="mensagem-content-preview">
                                <div className="content-header">
                                    <h5>Conteúdo da Mensagem</h5>
                                    <div className="content-stats">
                                        <span className="stat-item">
                                            {template.conteudo?.length || 0} caracteres
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="mensagem-iframe-container">
                                    <iframe
                                        ref={iframeRef}
                                        srcDoc={template.conteudo || ''}
                                        title={`Preview: ${template.nome}`}
                                        className="preview-iframe-mensagem"
                                        sandbox="allow-same-origin"
                                        onLoad={() => setIsLoading(false)}
                                    />
                                </div>
                            </div>

                            {/* INSTRUÇÕES DE USO */}
                            <div className="mensagem-usage-tips">
                                <h6>💡 Dicas de Uso</h6>
                                <ul>
                                    <li>Use variáveis como <code>{'{{nome_cliente}}'}</code> para personalização</li>
                                    <li>Mantenha o conteúdo conciso para melhor engajamento</li>
                                    <li>Teste a mensagem antes de enviar em produção</li>
                                </ul>
                            </div>
                        </div>
                    )}
                </fieldset>
            )}

            {/* BOTÕES DE AÇÃO NO FINAL */}
            <div className="form-actions">
                <button 
                    type="button" 
                    onClick={onClose}
                    className="action-button action-button-secondary"
                >
                    <ArrowLeft size={18} />
                    Voltar para Lista
                </button>
            </div>

            {/* ESTILOS ESPECÍFICOS PARA PREVIEW DE MENSAGEM */}
            <style jsx>{`
                .preview-mensagem-container {
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 0.5rem;
                    overflow: hidden;
                }

                .mensagem-preview-header {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    padding: 1.5rem;
                    background: #f8fafc;
                    border-bottom: 1px solid #e2e8f0;
                }

                .mensagem-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 48px;
                    height: 48px;
                    background: #dbeafe;
                    border-radius: 0.5rem;
                }

                .mensagem-info {
                    flex: 1;
                }

                .mensagem-preview-title {
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #1f2937;
                    margin: 0 0 0.5rem 0;
                }

                .mensagem-preview-meta {
                    display: flex;
                    gap: 1.5rem;
                    flex-wrap: wrap;
                }

                .meta-item {
                    font-size: 0.875rem;
                    color: #6b7280;
                }

                .mensagem-content-preview {
                    padding: 1.5rem;
                }

                .content-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1rem;
                }

                .content-header h5 {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #374151;
                    margin: 0;
                }

                .content-stats {
                    display: flex;
                    gap: 1rem;
                }

                .stat-item {
                    font-size: 0.875rem;
                    color: #6b7280;
                    background: #f3f4f6;
                    padding: 0.25rem 0.5rem;
                    border-radius: 0.25rem;
                }

                .mensagem-iframe-container {
                    border: 1px solid #e5e7eb;
                    border-radius: 0.375rem;
                    overflow: hidden;
                    background: white;
                }

                .preview-iframe-mensagem {
                    width: 100%;
                    height: 400px;
                    border: none;
                }

                .mensagem-usage-tips {
                    padding: 1rem 1.5rem;
                    background: #f0f9ff;
                    border-top: 1px solid #e0f2fe;
                }

                .mensagem-usage-tips h6 {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: #0369a1;
                    margin: 0 0 0.5rem 0;
                }

                .mensagem-usage-tips ul {
                    margin: 0;
                    padding-left: 1.25rem;
                    font-size: 0.875rem;
                    color: #475569;
                }

                .mensagem-usage-tips li {
                    margin-bottom: 0.25rem;
                }

                .mensagem-usage-tips code {
                    background: #e2e8f0;
                    padding: 0.125rem 0.25rem;
                    border-radius: 0.25rem;
                    font-size: 0.75rem;
                    color: #334155;
                }

                @media print {
                    .form-header,
                    .form-section:not(:last-child),
                    .form-actions {
                        display: none !important;
                    }
                    
                    .preview-mensagem-container {
                        border: none;
                    }
                    
                    .mensagem-usage-tips {
                        display: none !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default TemplateMensagemPreview;